import { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { Plus, Settings, Package, Link, Layers, Pencil, Trash2, Users, Upload, Tag, Palette, Factory, FileText, Download, ClipboardList } from "lucide-react";
import PantoneLibrary from "../components/PantoneLibrary";
import StockItemSearch from "../components/StockItemSearch";
import BidsoReviewsTab from "../components/BidsoReviewsTab";
import BrandBomImportReviewsTab from "../components/BrandBomImportReviewsTab";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Single source of truth for RM UOM options — every UOM <Select> in this
// file maps over this list so the sets can't drift. Was the root cause of
// a Feb 2026 bug where "ROLL" silently disappeared from the BOM component
// dropdown while remaining on the RM master dropdown. Do NOT re-inline any
// of these lists — add new UOMs here.
const RM_UOM_OPTIONS = [
  { value: "PCS", label: "PCS (Pieces)" },
  { value: "KG", label: "KG (Kilograms)" },
  { value: "GM", label: "GM (Grams)" },
  { value: "MTR", label: "MTR (Meters)" },
  { value: "LTR", label: "LTR (Liters)" },
  { value: "SET", label: "SET" },
  { value: "BOX", label: "BOX" },
  { value: "ROLL", label: "ROLL" },
];

const TechOps = () => {
  // Read `?tab=` query param so deep-links from other pages (e.g. the
  // Colourway Composer's post-submit "Open Tech Ops review queue" CTA)
  // can jump straight to a specific tab.
  const _initialTab = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      const allowed = ["verticals", "models", "brands", "buyers", "licenses", "pantone", "rm-categories", "rm-bom", "bidso-reviews"];
      return allowed.includes(t) ? t : "verticals";
    } catch { return "verticals"; }
  })();
  const [activeTab, setActiveTab] = useState(_initialTab);
  
  // Master Data
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [licenses, setLicenses] = useState([]);
  
  // RM Categories & BOM
  const [rmCategories, setRmCategories] = useState([]);
  const [rmBoms, setRmBoms] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);

  // Per-tab loading flags so each tab can show its own spinner instead of
  // a global blocking load. Helps surface failures per-tab rather than
  // showing every count as (0).
  const [loading, setLoading] = useState({
    verticals: false, models: false, brands: false, buyers: false,
    licenses: false, rmCategories: false, rmBoms: false, rawMaterials: false,
  });
  // Tracks which tabs have completed at least one fetch — so re-clicking a
  // tab doesn't re-fetch redundantly, and the badge shows the real count.
  const [loaded, setLoaded] = useState({
    verticals: false, models: false, brands: false, buyers: false,
    licenses: false, rmCategories: false, rmBoms: false, rawMaterials: false,
  });

  // RM BOM server-side pagination state
  const [bomPage, setBomPage] = useState(1);
  const [bomPageSize, setBomPageSize] = useState(50);
  const [bomTotal, setBomTotal] = useState(0);
  const [bomTotalPages, setBomTotalPages] = useState(1);
  const [bomSearch, setBomSearch] = useState("");
  const [bomFilterLevel, setBomFilterLevel] = useState("ALL");
  const [bomFilterCategory, setBomFilterCategory] = useState("ALL");
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const rmBomsReqIdRef = useRef(0);
  
  // Dialogs
  const [showVerticalDialog, setShowVerticalDialog] = useState(false);
  const [showModelDialog, setShowModelDialog] = useState(false);
  const [showBrandDialog, setShowBrandDialog] = useState(false);
  const [showBuyerDialog, setShowBuyerDialog] = useState(false);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showBomDialog, setShowBomDialog] = useState(false);
  
  // Edit mode
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form Data
  const [verticalForm, setVerticalForm] = useState({ code: "", name: "", description: "" });
  const [modelForm, setModelForm] = useState({ vertical_id: "", code: "", name: "", description: "" });
  const [brandForm, setBrandForm] = useState({ code: "", name: "" });
  const [licenseForm, setLicenseForm] = useState({ code: "", name: "" });
  const [buyerForm, setBuyerForm] = useState({ name: "", gst: "", email: "", phone_no: "", poc_name: "" });
  const [categoryForm, setCategoryForm] = useState({ 
    code: "", name: "", description: "", 
    default_source_type: "PURCHASED", default_bom_level: 1,
    default_uom: "PCS", rm_id_prefix: "", description_columns: [],
    zoho_account_type: ""
  });
  const [bomForm, setBomForm] = useState({
    rm_id: "", category: "", bom_level: 2, output_qty: 1, output_uom: "PCS",
    yield_factor: 1.0, components: []
  });
  const [bomComponent, setBomComponent] = useState({
    component_rm_id: "", quantity: 0, uom: "KG", percentage: 0, wastage_factor: 1.0
  });
  
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  // Download functions for each tab
  const handleDownload = async (type) => {
    setDownloading(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      let data = [];
      let filename = '';
      let sheetName = '';
      
      switch(type) {
        case 'verticals':
          data = [['Code', 'Name', 'Description', 'Status']];
          verticals.forEach(v => data.push([v.code, v.name, v.description || '', v.status]));
          filename = 'verticals';
          sheetName = 'Verticals';
          break;
          
        case 'models':
          data = [['Code', 'Name', 'Vertical', 'Description', 'Status']];
          models.forEach(m => {
            const vert = verticals.find(v => v.id === m.vertical_id);
            data.push([m.code, m.name, vert?.name || '', m.description || '', m.status]);
          });
          filename = 'models';
          sheetName = 'Models';
          break;
          
        case 'brands':
          data = [['Code', 'Name', 'Status']];
          brands.forEach(b => data.push([b.code, b.name, b.status]));
          filename = 'brands';
          sheetName = 'Brands';
          break;
          
        case 'buyers':
          data = [['Customer Code', 'Name', 'GST', 'Email', 'Phone', 'POC Name', 'Status']];
          buyers.forEach(b => data.push([
            b.customer_code || b.code || '', 
            b.name, 
            b.gst || '', 
            b.email || '', 
            b.phone_no || '', 
            b.poc_name || '',
            b.status
          ]));
          filename = 'buyers';
          sheetName = 'Buyers';
          break;
          
        default:
          toast.error('Unknown download type');
          return;
      }
      
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`Downloaded ${data.length - 1} ${type}`);
      
    } catch (error) {
      toast.error('Failed to download');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  // ---- Per-tab fetchers ----
  // Each fetcher updates ONLY its own slice of state + flips its
  // loaded flag. Tabs lazy-load on first activation; the active tab's
  // data refetches after any CRUD mutation via the refetchActiveTab()
  // helper below.

  const fetchVerticals = async () => {
    setLoading(l => ({ ...l, verticals: true }));
    try {
      const res = await axios.get(`${API}/verticals`);
      setVerticals((res.data || []).filter(v => v.status === 'ACTIVE'));
    } catch (e) {
      toast.error("Failed to load verticals");
    } finally {
      setLoading(l => ({ ...l, verticals: false }));
      setLoaded(l => ({ ...l, verticals: true }));
    }
  };

  const fetchModels = async () => {
    setLoading(l => ({ ...l, models: true }));
    try {
      const res = await axios.get(`${API}/models`);
      setModels((res.data || []).filter(m => m.status === 'ACTIVE'));
    } catch (e) {
      toast.error("Failed to load models");
    } finally {
      setLoading(l => ({ ...l, models: false }));
      setLoaded(l => ({ ...l, models: true }));
    }
  };

  const fetchBrands = async () => {
    setLoading(l => ({ ...l, brands: true }));
    try {
      const res = await axios.get(`${API}/brands`);
      setBrands((res.data || []).filter(b => b.status === 'ACTIVE'));
    } catch (e) {
      toast.error("Failed to load brands");
    } finally {
      setLoading(l => ({ ...l, brands: false }));
      setLoaded(l => ({ ...l, brands: true }));
    }
  };

  const fetchBuyers = async () => {
    setLoading(l => ({ ...l, buyers: true }));
    try {
      const res = await axios.get(`${API}/buyers`);
      setBuyers((res.data || []).filter(b => b.status === 'ACTIVE'));
    } catch (e) {
      toast.error("Failed to load buyers");
    } finally {
      setLoading(l => ({ ...l, buyers: false }));
      setLoaded(l => ({ ...l, buyers: true }));
    }
  };

  const fetchLicenses = async () => {
    setLoading(l => ({ ...l, licenses: true }));
    try {
      const res = await axios.get(`${API}/licenses`);
      setLicenses((res.data || []).filter(l => l.status === 'ACTIVE'));
    } catch (e) {
      // Licenses endpoint may not exist in older deploys — fail soft.
      setLicenses([]);
    } finally {
      setLoading(l => ({ ...l, licenses: false }));
      setLoaded(l => ({ ...l, licenses: true }));
    }
  };

  const fetchRmCategories = async () => {
    setLoading(l => ({ ...l, rmCategories: true }));
    try {
      const res = await axios.get(`${API}/production/rm-categories`);
      setRmCategories(res.data || []);
    } catch (e) {
      toast.error("Failed to load RM categories");
      setRmCategories([]);
    } finally {
      setLoading(l => ({ ...l, rmCategories: false }));
      setLoaded(l => ({ ...l, rmCategories: true }));
    }
  };

  // Lightweight RM list — only `{rm_id, name, description, category}` —
  // used as a lookup map for BOM rows. Skips the full /raw-materials
  // payload (2800+ docs with category_data, branch enrichment, etc.).
  const fetchRawMaterialsLite = async () => {
    setLoading(l => ({ ...l, rawMaterials: true }));
    try {
      const res = await axios.get(`${API}/raw-materials?mode=list&page_size=20000`);
      setRawMaterials(res.data || []);
    } catch (e) {
      setRawMaterials([]);
    } finally {
      setLoading(l => ({ ...l, rawMaterials: false }));
      setLoaded(l => ({ ...l, rawMaterials: true }));
    }
  };

  // RM BOM — server-paginated. Reads current bomPage / bomPageSize /
  // bomSearch / bomFilterLevel / bomFilterCategory state.
  const fetchRmBoms = async () => {
    const myReqId = ++rmBomsReqIdRef.current;
    setLoading(l => ({ ...l, rmBoms: true }));
    try {
      const params = new URLSearchParams();
      params.set("paginated", "true");
      params.set("page", String(bomPage));
      params.set("page_size", String(bomPageSize));
      if (bomSearch.trim()) params.set("search", bomSearch.trim());
      if (bomFilterCategory && bomFilterCategory !== "ALL") params.set("category", bomFilterCategory);
      if (bomFilterLevel && bomFilterLevel !== "ALL") params.set("bom_level", bomFilterLevel);
      const res = await axios.get(`${API}/rm-bom?${params.toString()}`);
      if (myReqId !== rmBomsReqIdRef.current) return;
      const d = res.data || {};
      setRmBoms(d.items || []);
      setBomTotal(d.total || 0);
      setBomTotalPages(d.total_pages || 1);
    } catch (e) {
      if (myReqId !== rmBomsReqIdRef.current) return;
      toast.error("Failed to load RM BOMs");
      setRmBoms([]);
    } finally {
      if (myReqId === rmBomsReqIdRef.current) {
        setLoading(l => ({ ...l, rmBoms: false }));
        setLoaded(l => ({ ...l, rmBoms: true }));
      }
    }
  };

  // Re-runs the fetchers tied to the currently-visible tab. Replaces the
  // old fetchAllData() that re-pulled everything after each mutation.
  const refetchActiveTab = () => {
    if (activeTab === "verticals") fetchVerticals();
    else if (activeTab === "models") { fetchModels(); if (!loaded.verticals) fetchVerticals(); }
    else if (activeTab === "brands") fetchBrands();
    else if (activeTab === "buyers") fetchBuyers();
    else if (activeTab === "licenses") fetchLicenses();
    else if (activeTab === "rm-categories") fetchRmCategories();
    else if (activeTab === "rm-bom") {
      fetchRmBoms();
      if (!loaded.rmCategories) fetchRmCategories();
      if (!loaded.rawMaterials) fetchRawMaterialsLite();
    }
  };

  // Initial load — only the initial tab's data + verticals (needed for
  // most other tabs' label resolution).
  useEffect(() => {
    if (!loaded.verticals) fetchVerticals();
  }, []);

  // Lazy-load: fetch a tab's data on first activation only.
  useEffect(() => {
    if (activeTab === "verticals" && !loaded.verticals) fetchVerticals();
    else if (activeTab === "models") {
      if (!loaded.models) fetchModels();
      if (!loaded.verticals) fetchVerticals();
    }
    else if (activeTab === "brands" && !loaded.brands) fetchBrands();
    else if (activeTab === "buyers" && !loaded.buyers) fetchBuyers();
    else if (activeTab === "licenses" && !loaded.licenses) fetchLicenses();
    else if (activeTab === "rm-categories" && !loaded.rmCategories) fetchRmCategories();
    else if (activeTab === "rm-bom") {
      if (!loaded.rmBoms) fetchRmBoms();
      if (!loaded.rmCategories) fetchRmCategories();
      if (!loaded.rawMaterials) fetchRawMaterialsLite();
    }
  }, [activeTab]);

  // RM BOM tab — refetch whenever page / filters change AFTER the tab
  // has been loaded once.
  useEffect(() => {
    if (loaded.rmBoms || activeTab === "rm-bom") {
      fetchRmBoms();
    }
  }, [bomPage, bomPageSize, bomFilterLevel, bomFilterCategory]);

  // Server-side search with debounce
  useEffect(() => {
    if (!loaded.rmBoms && activeTab !== "rm-bom") return;
    const t = setTimeout(() => {
      setBomPage(1);
      fetchRmBoms();
    }, 350);
    return () => clearTimeout(t);
  }, [bomSearch]);

  // Legacy compatibility — callers throughout the file invoke
  // fetchAllData() after a mutation. Map it onto the per-tab refresh.
  const fetchAllData = () => refetchActiveTab();

  // Vertical CRUD
  const handleCreateVertical = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/verticals/${editingItem.id}`, verticalForm);
        toast.success("Vertical updated");
      } else {
        await axios.post(`${API}/verticals`, verticalForm);
        toast.success("Vertical created");
      }
      setShowVerticalDialog(false);
      setVerticalForm({ code: "", name: "", description: "" });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vertical");
    }
  };

  const handleEditVertical = (v) => {
    setVerticalForm({ code: v.code, name: v.name, description: v.description || "" });
    setEditingItem(v);
    setShowVerticalDialog(true);
  };

  const handleDeleteVertical = async (id) => {
    try {
      await axios.delete(`${API}/verticals/${id}`);
      toast.success("Vertical deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vertical");
    }
    setDeleteConfirm(null);
  };

  // Model CRUD
  const handleCreateModel = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/models/${editingItem.id}`, modelForm);
        toast.success("Model updated");
      } else {
        await axios.post(`${API}/models`, modelForm);
        toast.success("Model created");
      }
      setShowModelDialog(false);
      setModelForm({ vertical_id: "", code: "", name: "", description: "" });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save model");
    }
  };

  const handleEditModel = (m) => {
    setModelForm({ vertical_id: m.vertical_id, code: m.code, name: m.name, description: m.description || "" });
    setEditingItem(m);
    setShowModelDialog(true);
  };

  const handleDeleteModel = async (id) => {
    try {
      await axios.delete(`${API}/models/${id}`);
      toast.success("Model deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete model");
    }
    setDeleteConfirm(null);
  };

  // Brand CRUD
  const handleCreateBrand = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/brands/${editingItem.id}`, brandForm);
        toast.success("Brand updated");
      } else {
        await axios.post(`${API}/brands`, brandForm);
        toast.success("Brand created");
      }
      setShowBrandDialog(false);
      setBrandForm({ code: "", name: "" });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save brand");
    }
  };

  const handleEditBrand = (b) => {
    setBrandForm({ code: b.code, name: b.name });
    setEditingItem(b);
    setShowBrandDialog(true);
  };

  const handleDeleteBrand = async (id) => {
    try {
      await axios.delete(`${API}/brands/${id}`);
      toast.success("Brand deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete brand");
    }
    setDeleteConfirm(null);
  };

  // License CRUD (Feb 2026)
  const handleSaveLicense = async () => {
    try {
      const payload = {
        code: (licenseForm.code || "").trim().toUpperCase(),
        name: (licenseForm.name || "").trim(),
      };
      if (!payload.code || !payload.name) {
        toast.error("License code and name are required");
        return;
      }
      if (editingItem) {
        await axios.put(`${API}/licenses/${editingItem.id}`, payload);
        toast.success("License updated");
      } else {
        await axios.post(`${API}/licenses`, payload);
        toast.success("License created");
      }
      setShowLicenseDialog(false);
      setLicenseForm({ code: "", name: "" });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save license");
    }
  };

  const handleEditLicense = (lic) => {
    setLicenseForm({ code: lic.code, name: lic.name });
    setEditingItem(lic);
    setShowLicenseDialog(true);
  };

  const handleDeleteLicense = async (id) => {
    try {
      await axios.delete(`${API}/licenses/${id}`);
      toast.success("License deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete license");
    }
    setDeleteConfirm(null);
  };

  // Buyer CRUD
  const handleCreateBuyer = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/buyers/${editingItem.id}`, buyerForm);
        toast.success("Buyer updated");
      } else {
        await axios.post(`${API}/buyers`, buyerForm);
        toast.success("Buyer created with auto-generated code");
      }
      setShowBuyerDialog(false);
      setBuyerForm({ name: "", gst: "", email: "", phone_no: "", poc_name: "" });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save buyer");
    }
  };

  const handleEditBuyer = (b) => {
    setBuyerForm({ 
      name: b.name || "", 
      gst: b.gst || "", 
      email: b.email || "", 
      phone_no: b.phone_no || "", 
      poc_name: b.poc_name || "" 
    });
    setEditingItem(b);
    setShowBuyerDialog(true);
  };

  const handleDeleteBuyer = async (id) => {
    try {
      await axios.delete(`${API}/buyers/${id}`);
      toast.success("Buyer deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete buyer");
    }
    setDeleteConfirm(null);
  };

  const handleBuyerImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const data = await uploadWithReceipt(`${API}/buyers/bulk-import`, formData, {
        axiosConfig: { headers: { 'Content-Type': 'multipart/form-data' } },
        showToast: false,
      });
      
      // Check for duplicates (preserved logic)
      if (data.duplicates && data.duplicates.length > 0) {
        const dupCount = data.duplicates.length;
        const dupList = data.duplicates.slice(0, 5).map(d => d.name).join(", ");
        toast.error(
          `${data.created} created, ${dupCount} skipped (duplicates). No overwrites allowed.\nDuplicates: ${dupList}${dupCount > 5 ? '...' : ''} — Receipt downloaded.`,
          { duration: 8000 }
        );
      } else {
        toast.success((data.message || "Import complete") + " — Receipt downloaded.");
      }
      
      setShowImportDialog(false);
      fetchAllData();
    } catch (error) {
      const errData = error.response?.data;
      if (errData?.duplicates && errData.duplicates.length > 0) {
        const dupCount = errData.duplicates.length;
        const dupList = errData.duplicates.slice(0, 5).map(d => d.name).join(", ");
        toast.error(
          `Upload blocked: ${dupCount} duplicate(s) found. No overwrites allowed.\nDuplicates: ${dupList}${dupCount > 5 ? '...' : ''}`,
          { duration: 8000 }
        );
      } else {
        toast.error(errData?.detail || errData?.message || "Import failed");
      }
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Feb 2026 — Buyer Address bulk import (multi-address per customer).
  const handleAddressImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await uploadWithReceipt(`${API}/buyers/addresses/bulk-import`, formData, {
        axiosConfig: { headers: { "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      toast.success(
        `${data.message || "Address import complete"} — Receipt downloaded.`,
        { duration: 6000 },
      );
      setShowImportDialog(false);
      fetchBuyers();
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.detail || d?.message || err.message || "Address import failed");
    } finally {
      setImportLoading(false);
      // Reset the input so the same file can be re-picked.
      e.target.value = "";
    }
  };

  // Feb 2026 — Flattened child-row export ("Download Addresses").
  const handleDownloadAddresses = () => {
    try {
      const rows = [[
        "Customer Code", "Customer Name", "Address Type", "Address Label",
        "GSTIN", "Contact Name", "Contact Phone", "Address Line 1", "Address Line 2",
        "City", "State", "Pincode", "Country", "Is Default", "Status",
      ]];
      buyers.forEach(b => {
        (b.addresses || []).forEach(a => {
          rows.push([
            b.customer_code || "",
            b.name || "",
            a.address_type || "",
            a.label || "",
            a.gstin || "",
            a.contact_name || "",
            a.contact_phone || "",
            a.line1 || "",
            a.line2 || "",
            a.city || "",
            a.state || "",
            a.pincode || "",
            a.country || "India",
            a.is_default ? "Y" : "N",
            a.status || "ACTIVE",
          ]);
        });
      });
      // Reuse the CSV-download utility already used by other tabs.
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `buyer-addresses-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rows.length - 1} address row(s)`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  };

  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || id;
  const getBuyerName = (id) => buyers.find(b => b.id === id)?.name || id;
  const getRmName = (rmId) => rawMaterials.find(r => r.rm_id === rmId)?.description || rmId;

  // RM Category CRUD
  const handleSaveCategory = async () => {
    try {
      const payload = {
        ...categoryForm,
        rm_id_prefix: categoryForm.rm_id_prefix || categoryForm.code.toUpperCase(),
        description_columns: categoryForm.description_columns || []
      };
      
      if (editingItem) {
        await axios.put(`${API}/production/rm-categories/${editingItem.code}`, payload);
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/production/rm-categories`, payload);
        toast.success("Category created");
      }
      setShowCategoryDialog(false);
      setCategoryForm({ 
        code: "", name: "", description: "", 
        default_source_type: "PURCHASED", default_bom_level: 1,
        default_uom: "PCS", rm_id_prefix: "", description_columns: [],
        zoho_account_type: ""
      });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    }
  };

  const openEditCategory = (cat) => {
    setEditingItem(cat);
    setCategoryForm({
      code: cat.code,
      name: cat.name,
      description: cat.description || "",
      default_source_type: cat.default_source_type,
      default_bom_level: cat.default_bom_level,
      default_uom: cat.default_uom || "PCS",
      rm_id_prefix: cat.rm_id_prefix || cat.code,
      description_columns: cat.description_columns || [],
      zoho_account_type: cat.zoho_account_type || ""
    });
    setShowCategoryDialog(true);
  };

  // Add/Remove description column
  const addDescriptionColumn = () => {
    setCategoryForm({
      ...categoryForm,
      description_columns: [
        ...categoryForm.description_columns,
        { key: "", label: "", type: "text", required: false, options: [], include_in_name: false, order: categoryForm.description_columns.length }
      ]
    });
  };

  const updateDescriptionColumn = (index, field, value) => {
    const updated = [...categoryForm.description_columns];
    updated[index] = { ...updated[index], [field]: value };
    setCategoryForm({ ...categoryForm, description_columns: updated });
  };

  const removeDescriptionColumn = (index) => {
    setCategoryForm({
      ...categoryForm,
      description_columns: categoryForm.description_columns.filter((_, i) => i !== index)
    });
  };

  // ─── Feb 2026 (P2) — Manage Options dialog for `select` columns ───
  // Wraps the generic endpoints:
  //   POST   /api/rm-categories/{code}/columns/{key}/options  {value}
  //   DELETE /api/rm-categories/{code}/columns/{key}/options  {value}
  //   PUT    /api/rm-categories/{code}/columns/{key}/options  {options[]}
  // Changes persist immediately (independent of the category form Save).
  const [optionsDialog, setOptionsDialog] = useState({
    open: false, code: "", columnKey: "", columnLabel: "",
    options: [], search: "", newOption: "",
    bulkMode: false, bulkText: "",
    confirmBulk: false, inFlight: false,
  });

  const openOptionsDialog = (code, col) => {
    setOptionsDialog({
      open: true, code, columnKey: col.key,
      columnLabel: col.label || col.key,
      options: [...(col.options || [])],
      search: "", newOption: "",
      bulkMode: false, bulkText: (col.options || []).join("\n"),
      confirmBulk: false, inFlight: false,
    });
  };

  // Sync a fresh options array back into the open editor and the master
  // category list — count badges + editor's Select column stay live
  // without a full page reload.
  const _syncOptionsBack = (nextOptions) => {
    setOptionsDialog(prev => ({ ...prev, options: nextOptions, bulkText: nextOptions.join("\n") }));
    setCategoryForm(prev => ({
      ...prev,
      description_columns: (prev.description_columns || []).map(c =>
        c.key === optionsDialog.columnKey ? { ...c, options: nextOptions } : c
      ),
    }));
    // Best-effort refetch so the category list badge reflects the change.
    fetchRmCategories?.().catch?.(() => {});
  };

  const handleAddOption = async () => {
    const v = (optionsDialog.newOption || "").trim();
    if (!v) return;
    setOptionsDialog(prev => ({ ...prev, inFlight: true }));
    try {
      const res = await axios.post(
        `${API}/rm-categories/${optionsDialog.code}/columns/${optionsDialog.columnKey}/options`,
        { value: v },
      );
      _syncOptionsBack(res.data.options || []);
      setOptionsDialog(prev => ({ ...prev, newOption: "", inFlight: false }));
      toast.success(res.data.added ? `Added "${res.data.value}"` : `Already exists as "${res.data.value}"`);
    } catch (e) {
      setOptionsDialog(prev => ({ ...prev, inFlight: false }));
      toast.error(e?.response?.data?.detail || "Failed to add option");
    }
  };

  const handleDeleteOption = async (value) => {
    setOptionsDialog(prev => ({ ...prev, inFlight: true }));
    try {
      const res = await axios.delete(
        `${API}/rm-categories/${optionsDialog.code}/columns/${optionsDialog.columnKey}/options`,
        { data: { value } },
      );
      _syncOptionsBack(res.data.options || []);
      setOptionsDialog(prev => ({ ...prev, inFlight: false }));
      toast.success(res.data.removed ? `Removed "${value}"` : `"${value}" was not in the list`);
    } catch (e) {
      setOptionsDialog(prev => ({ ...prev, inFlight: false }));
      toast.error(e?.response?.data?.detail || "Failed to delete option");
    }
  };

  const handleBulkReplace = async () => {
    const lines = (optionsDialog.bulkText || "")
      .split("\n").map(l => l.trim()).filter(Boolean);
    setOptionsDialog(prev => ({ ...prev, inFlight: true, confirmBulk: false }));
    try {
      const res = await axios.put(
        `${API}/rm-categories/${optionsDialog.code}/columns/${optionsDialog.columnKey}/options`,
        { options: lines },
      );
      _syncOptionsBack(res.data.options || []);
      setOptionsDialog(prev => ({ ...prev, bulkMode: false, inFlight: false }));
      toast.success(`Replaced list with ${(res.data.options || []).length} options`);
    } catch (e) {
      setOptionsDialog(prev => ({ ...prev, inFlight: false }));
      toast.error(e?.response?.data?.detail || "Failed to replace options");
    }
  };

  // RM BOM CRUD
  const [bomUploadOpen, setBomUploadOpen] = useState(false);
  const [bomUploadMode, setBomUploadMode] = useState("skip");
  const [bomUploading, setBomUploading] = useState(false);
  const bomFileRef = useRef(null);
  
  const handleBomBulkUpload = async () => {
    const file = bomFileRef.current?.files?.[0];
    if (!file) { toast.error("Select a file"); return; }
    
    setBomUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const d = await uploadWithReceipt(
        `${API}/rm-bom/bulk-upload?mode=${bomUploadMode}`,
        formData,
        { axiosConfig: { headers: { "Content-Type": "multipart/form-data" } }, showToast: false }
      );
      
      toast.success((d.message || "Upload complete") + " — Receipt downloaded.");
      
      if (d.errors?.length > 0) {
        toast.error(`${d.errors.length} errors: ${d.errors.slice(0, 3).join(", ")}`);
      }
      
      setBomUploadOpen(false);
      if (bomFileRef.current) bomFileRef.current.value = "";
      fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Upload failed: ${msg}`);
    }
    setBomUploading(false);
  };

  const downloadBomTemplate = () => {
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    const data = [
      ["RM ID", "BOM RM ID", "Weight in gm / Pc", "Wastage %"],
      ["INP_001", "POLY_001", 100, "2%"],
      ["INP_001", "POLY_002", 100, "2%"],
      ["INP_001", "MB_001", 20, "2%"],
      ["INMFAB_001", "MTL_001", 100, "2%"],
      ["INMFAB_001", "MTL_002", 50, "2%"],
      ["INM_001", "INMFAB_001", 1, "0%"],
      ["INM_001", "PWD_001", 50, "2%"]
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, "RM BOM Template");
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'RM_BOM_Upload_Template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveBom = async () => {
    try {
      if (bomForm.components.length === 0) {
        toast.error("Add at least one component to the BOM");
        return;
      }
      
      const payload = {
        ...bomForm,
        rm_id: bomForm.rm_id.toUpperCase()
      };
      
      if (editingItem) {
        await axios.put(`${API}/rm-bom/${editingItem.rm_id}`, payload);
        toast.success("BOM updated");
      } else {
        await axios.post(`${API}/rm-bom`, payload);
        toast.success("BOM created");
      }
      setShowBomDialog(false);
      setBomForm({ rm_id: "", category: "", bom_level: 2, output_qty: 1, output_uom: "PCS", yield_factor: 1.0, components: [] });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save BOM");
    }
  };

  const handleDeleteBom = async (rmId) => {
    try {
      await axios.delete(`${API}/rm-bom/${rmId}`);
      toast.success("BOM deleted");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete BOM");
    }
  };

  const openEditBom = (bom) => {
    setEditingItem(bom);
    setBomForm({
      rm_id: bom.rm_id,
      category: bom.category,
      bom_level: bom.bom_level,
      output_qty: bom.output_qty,
      output_uom: bom.output_uom,
      yield_factor: bom.yield_factor,
      components: bom.components || []
    });
    setShowBomDialog(true);
  };

  const addBomComponent = () => {
    if (!bomComponent.component_rm_id) {
      toast.error("Select a component RM");
      return;
    }
    if (bomForm.components.find(c => c.component_rm_id === bomComponent.component_rm_id.toUpperCase())) {
      toast.error("Component already added");
      return;
    }
    const rm = rawMaterials.find(r => r.rm_id === bomComponent.component_rm_id.toUpperCase());
    setBomForm({
      ...bomForm,
      components: [...bomForm.components, {
        ...bomComponent,
        component_rm_id: bomComponent.component_rm_id.toUpperCase(),
        component_name: rm?.description || bomComponent.component_rm_id
      }]
    });
    setBomComponent({ component_rm_id: "", quantity: 0, uom: "KG", percentage: 0, wastage_factor: 1.0 });
  };

  const removeBomComponent = (rmId) => {
    setBomForm({
      ...bomForm,
      components: bomForm.components.filter(c => c.component_rm_id !== rmId)
    });
  };

  const openAddDialog = (type) => {
    setEditingItem(null);
    if (type === 'vertical') {
      setVerticalForm({ code: "", name: "", description: "" });
      setShowVerticalDialog(true);
    } else if (type === 'model') {
      setModelForm({ vertical_id: "", code: "", name: "", description: "" });
      setShowModelDialog(true);
    } else if (type === 'brand') {
      setBrandForm({ code: "", name: "" });
      setShowBrandDialog(true);
    } else if (type === 'buyer') {
      setBuyerForm({ name: "", gst: "", email: "", phone_no: "", poc_name: "" });
      setShowBuyerDialog(true);
    } else if (type === 'license') {
      setLicenseForm({ code: "", name: "" });
      setShowLicenseDialog(true);
    } else if (type === 'category') {
      setCategoryForm({ code: "", name: "", description: "", default_source_type: "PURCHASED", default_bom_level: 1, default_uom: "PCS", rm_id_prefix: "", description_columns: [], zoho_account_type: "" });
      setShowCategoryDialog(true);
    } else if (type === 'bom') {
      setBomForm({ rm_id: "", category: "", bom_level: 2, output_qty: 1, output_uom: "PCS", yield_factor: 1.0, components: [] });
      setShowBomDialog(true);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="tech-ops-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight uppercase">Tech Ops</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">Master Data Management</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="verticals" className="uppercase text-xs tracking-wide">
            <Layers className="w-4 h-4 mr-2" />
            Verticals ({loading.verticals && !loaded.verticals ? "…" : verticals.length})
          </TabsTrigger>
          <TabsTrigger value="models" className="uppercase text-xs tracking-wide">
            <Package className="w-4 h-4 mr-2" />
            Models ({loading.models && !loaded.models ? "…" : models.length})
          </TabsTrigger>
          <TabsTrigger value="brands" className="uppercase text-xs tracking-wide">
            <Settings className="w-4 h-4 mr-2" />
            Brands ({loading.brands && !loaded.brands ? "…" : brands.length})
          </TabsTrigger>
          {/* Feb 2026 — Buyer tab relocated to Demand → Buyer Management.
              See services/seed_rbac.py (Buyer:* now grants DEMAND_PLANNER
              write, TECH_OPS_ENGINEER lost CREATE/UPDATE) and
              components/BuyerManagementTab.jsx for the new home. */}
          <TabsTrigger value="licenses" className="uppercase text-xs tracking-wide" data-testid="tab-licenses">
            <Tag className="w-4 h-4 mr-2" />
            Licenses ({loading.licenses && !loaded.licenses ? "…" : licenses.length})
          </TabsTrigger>
          <TabsTrigger value="pantone" className="uppercase text-xs tracking-wide">
            <Palette className="w-4 h-4 mr-2" />
            Pantone Library
          </TabsTrigger>
          <TabsTrigger value="rm-categories" className="uppercase text-xs tracking-wide">
            <Factory className="w-4 h-4 mr-2" />
            RM Categories ({loading.rmCategories ? "…" : rmCategories.length})
          </TabsTrigger>
          <TabsTrigger value="rm-bom" className="uppercase text-xs tracking-wide">
            <FileText className="w-4 h-4 mr-2" />
            RM BOM ({loading.rmBoms && !loaded.rmBoms ? "…" : (loaded.rmBoms ? bomTotal : "—")})
          </TabsTrigger>
          <TabsTrigger value="bidso-reviews" className="uppercase text-xs tracking-wide" data-testid="tab-bidso-reviews">
            <ClipboardList className="w-4 h-4 mr-2" />
            Bidso Reviews
          </TabsTrigger>
          {/* Jul 2026 — Brand-specific BOM import reviews (Demand Planner → Tech Ops). */}
          <TabsTrigger value="brand-bom-reviews" className="uppercase text-xs tracking-wide" data-testid="tab-brand-bom-reviews">
            <ClipboardList className="w-4 h-4 mr-2" />
            Brand BOM Reviews
          </TabsTrigger>
        </TabsList>

        {/* Verticals Tab */}
        <TabsContent value="verticals">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold">Product Verticals</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownload('verticals')} disabled={downloading || verticals.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                {downloading ? "..." : `Download (${verticals.length})`}
              </Button>
              <Button onClick={() => openAddDialog('vertical')} className="uppercase text-xs tracking-wide" data-testid="add-vertical-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Vertical
              </Button>
            </div>
          </div>
          
          <div className="border rounded-sm">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Code</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Name</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Description</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Models</th>
                  <th className="h-10 px-4 text-right font-mono text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {verticals.map((v) => (
                  <tr key={v.id} className="border-t">
                    <td className="p-4 font-mono font-bold">{v.code}</td>
                    <td className="p-4">{v.name}</td>
                    <td className="p-4 text-sm text-muted-foreground">{v.description}</td>
                    <td className="p-4 font-mono">{models.filter(m => m.vertical_id === v.id).length}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEditVertical(v)} data-testid={`edit-vertical-${v.code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'vertical', id: v.id, name: v.name })} data-testid={`delete-vertical-${v.code}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {verticals.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No verticals defined</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Models Tab */}
        <TabsContent value="models">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold">Product Models</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownload('models')} disabled={downloading || models.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                {downloading ? "..." : `Download (${models.length})`}
              </Button>
              <Button onClick={() => openAddDialog('model')} className="uppercase text-xs tracking-wide" data-testid="add-model-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Model
              </Button>
            </div>
          </div>
          
          <div className="border rounded-sm">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Vertical</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Code</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Name</th>
                  <th className="h-10 px-4 text-right font-mono text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="p-4 text-sm">{getVerticalName(m.vertical_id)}</td>
                    <td className="p-4 font-mono font-bold">{m.code}</td>
                    <td className="p-4">{m.name}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEditModel(m)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'model', id: m.id, name: m.name })}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {models.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No models defined</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Brands Tab */}
        <TabsContent value="brands">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold">Brands</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownload('brands')} disabled={downloading || brands.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                {downloading ? "..." : `Download (${brands.length})`}
              </Button>
              <Button onClick={() => openAddDialog('brand')} className="uppercase text-xs tracking-wide" data-testid="add-brand-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Brand
              </Button>
            </div>
          </div>
          
          <div className="border rounded-sm">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Code</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Brand Name</th>
                  <th className="h-10 px-4 text-right font-mono text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.id} className="border-t">
                    <td className="p-4 font-mono font-bold">{b.code}</td>
                    <td className="p-4">{b.name}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEditBrand(b)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'brand', id: b.id, name: b.name })}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {brands.length === 0 && (
                  <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No brands defined</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Buyers Tab — Feb 2026: relocated to Demand → Buyer Management.
            See components/BuyerManagementTab.jsx. Handlers/state below
            (handleCreateBuyer, buyerForm, showBuyerDialog, etc.) are
            intentionally left in place as dead code — a follow-up
            pass will trim them once no other TechOps tab references
            them. Route is still `/techops` for every OTHER tab in
            this file (verticals, models, brands, licenses, pantone,
            RM cats, BOM, Bidso reviews). */}

        {/* Licenses Tab (Feb 2026) */}
        <TabsContent value="licenses">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold">Licensed IPs</h2>
              <p className="text-xs text-zinc-500 mt-1 font-mono">
                Catalogue of licensed brands/IPs. Codes are appended into Bidso SKU IDs as <span className="bg-zinc-100 px-1 rounded">{`{Vertical}_{Model}_{License}_{Number}`}</span>.
              </p>
            </div>
            <Button onClick={() => openAddDialog('license')} className="uppercase text-xs tracking-wide" data-testid="add-license-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add License
            </Button>
          </div>

          <div className="border rounded-sm">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">Code</th>
                  <th className="h-10 px-4 text-left font-mono text-xs uppercase">License Name</th>
                  <th className="h-10 px-4 text-right font-mono text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((lic) => (
                  <tr key={lic.id} className="border-t" data-testid={`license-row-${lic.code}`}>
                    <td className="p-4 font-mono font-bold">{lic.code}</td>
                    <td className="p-4">{lic.name}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEditLicense(lic)} data-testid={`edit-license-${lic.code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'license', id: lic.id, name: lic.name })} data-testid={`delete-license-${lic.code}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {licenses.length === 0 && (
                  <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No licenses defined yet. Add one to start tagging Bidso SKUs as licensed.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Pantone Library Tab */}
        <TabsContent value="pantone">
          <PantoneLibrary />
        </TabsContent>

        {/* RM Categories Tab */}
        <TabsContent value="rm-categories">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold">RM Categories</h2>
              <p className="text-sm text-muted-foreground">Define source types, BOM levels, UOM, and description columns for raw material categories</p>
            </div>
            <Button onClick={() => openAddDialog('category')} className="uppercase text-xs tracking-wide" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs uppercase font-bold">Code</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Name</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">UOM</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">ID Prefix</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Source Type</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Zoho Acct</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Level</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Columns</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Status</th>
                  <th className="text-right p-3 text-xs uppercase font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rmCategories.map(cat => (
                  <tr key={cat.code} className="hover:bg-gray-50" data-testid={`category-row-${cat.code}`}>
                    <td className="p-3 font-mono font-bold">{cat.code}</td>
                    <td className="p-3">{cat.name}</td>
                    <td className="p-3 text-center font-mono text-sm">{cat.default_uom || 'PCS'}</td>
                    <td className="p-3 text-center font-mono text-sm">{cat.rm_id_prefix || cat.code}_###</td>
                    <td className="p-3 text-center">
                      <Badge variant={cat.default_source_type === 'MANUFACTURED' ? 'default' : cat.default_source_type === 'BOTH' ? 'secondary' : 'outline'}>
                        {cat.default_source_type}
                      </Badge>
                    </td>
                    <td className="p-3 text-center text-xs">
                      {cat.zoho_account_type ? (
                        <span className="text-gray-700">{cat.zoho_account_type}</span>
                      ) : (
                        <span className="text-muted-foreground italic">— not set —</span>
                      )}
                    </td>
                    <td className="p-3 text-center font-mono">L{cat.default_bom_level}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className="text-xs">
                        {cat.description_columns?.length || 0} cols
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant={cat.is_active ? 'default' : 'destructive'}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditCategory(cat)} data-testid={`edit-rm-category-${cat.code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {rmCategories.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No categories defined</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* RM BOM Tab */}
        <TabsContent value="rm-bom">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold">RM Bill of Materials</h2>
              <p className="text-sm text-muted-foreground">
                Define component recipes for manufactured RMs
                {loaded.rmBoms && (
                  <span className="ml-2 text-xs font-mono">
                    · {bomTotal.toLocaleString()} BOM{bomTotal === 1 ? "" : "s"} total
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="secondary" 
                onClick={() => window.open(`${API}/rm-bom/export?level=2`, '_blank')} 
                className="uppercase text-xs tracking-wide"
                data-testid="export-l2-bom-btn"
                title="Download all L2 (INP) RMs and their BOMs"
              >
                <Download className="w-4 h-4 mr-2" />
                Export L2
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => window.open(`${API}/rm-bom/export?level=3`, '_blank')} 
                className="uppercase text-xs tracking-wide"
                data-testid="export-l3-bom-btn"
                title="Download all L3 (INM) RMs and their BOMs"
              >
                <Download className="w-4 h-4 mr-2" />
                Export L3
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => window.open(`${API}/rm-bom/export?level=all`, '_blank')} 
                className="uppercase text-xs tracking-wide"
                data-testid="export-all-bom-btn"
                title="Download all L2 + L3 RMs and their BOMs"
              >
                <Download className="w-4 h-4 mr-2" />
                Export L2 + L3
              </Button>
              <PermButton entity="BOM" action="UPDATE" variant="outline" onClick={() => setBomUploadOpen(true)} className="uppercase text-xs tracking-wide" data-testid="bulk-upload-bom-btn">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </PermButton>
              <PermButton entity="BOM" action="CREATE" onClick={() => openAddDialog('bom')} className="uppercase text-xs tracking-wide" data-testid="add-bom-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add BOM
              </PermButton>
            </div>
          </div>

          {/* Filters & search */}
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <Input
              data-testid="rm-bom-search-input"
              placeholder="Search by RM ID or name…"
              value={bomSearch}
              onChange={(e) => setBomSearch(e.target.value)}
              className="w-64"
            />
            <Select value={bomFilterLevel} onValueChange={(v) => { setBomFilterLevel(v); setBomPage(1); }}>
              <SelectTrigger className="w-36" data-testid="rm-bom-filter-level">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Levels</SelectItem>
                <SelectItem value="2">L2 (INP)</SelectItem>
                <SelectItem value="3">L3 (INM)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={bomFilterCategory} onValueChange={(v) => { setBomFilterCategory(v); setBomPage(1); }}>
              <SelectTrigger className="w-44" data-testid="rm-bom-filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Categories</SelectItem>
                {rmCategories.map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(bomPageSize)} onValueChange={(v) => { setBomPageSize(Number(v)); setBomPage(1); }}>
              <SelectTrigger className="w-32" data-testid="rm-bom-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
                <SelectItem value="200">200 / page</SelectItem>
              </SelectContent>
            </Select>
            {loading.rmBoms && (
              <span className="text-xs text-muted-foreground font-mono">Loading…</span>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs uppercase font-bold">RM ID</th>
                  <th className="text-left p-3 text-xs uppercase font-bold">Name</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Category</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Level</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Components</th>
                  <th className="text-center p-3 text-xs uppercase font-bold">Yield</th>
                  <th className="text-right p-3 text-xs uppercase font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rmBoms.map(bom => (
                  <tr key={bom.rm_id} className="hover:bg-gray-50" data-testid={`bom-row-${bom.rm_id}`}>
                    <td className="p-3 font-mono font-bold">{bom.rm_id}</td>
                    <td className="p-3">{bom.rm_name || getRmName(bom.rm_id)}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline">{bom.category}</Badge>
                    </td>
                    <td className="p-3 text-center font-mono">L{bom.bom_level}</td>
                    <td className="p-3 text-center">
                      <span className="text-sm">{bom.components?.length || 0} items</span>
                    </td>
                    <td className="p-3 text-center font-mono">{((bom.yield_factor || 1) * 100).toFixed(0)}%</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditBom(bom)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteBom(bom.rm_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {rmBoms.length === 0 && !loading.rmBoms && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                    {bomSearch || bomFilterLevel !== "ALL" || bomFilterCategory !== "ALL"
                      ? "No BOMs match your filters."
                      : "No BOMs defined. Create BOMs for manufactured RMs."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {loaded.rmBoms && bomTotal > 0 && (
            <div className="flex justify-between items-center mt-3 text-sm" data-testid="rm-bom-pagination">
              <div className="text-muted-foreground font-mono text-xs">
                Showing {((bomPage - 1) * bomPageSize) + 1}–{Math.min(bomPage * bomPageSize, bomTotal)} of {bomTotal.toLocaleString()}
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline" size="sm"
                  disabled={bomPage <= 1 || loading.rmBoms}
                  onClick={() => setBomPage(1)}
                  data-testid="rm-bom-page-first"
                >First</Button>
                <Button
                  variant="outline" size="sm"
                  disabled={bomPage <= 1 || loading.rmBoms}
                  onClick={() => setBomPage(p => Math.max(1, p - 1))}
                  data-testid="rm-bom-page-prev"
                >Prev</Button>
                <span className="font-mono text-xs px-2">
                  Page {bomPage} / {bomTotalPages}
                </span>
                <Button
                  variant="outline" size="sm"
                  disabled={bomPage >= bomTotalPages || loading.rmBoms}
                  onClick={() => setBomPage(p => Math.min(bomTotalPages, p + 1))}
                  data-testid="rm-bom-page-next"
                >Next</Button>
                <Button
                  variant="outline" size="sm"
                  disabled={bomPage >= bomTotalPages || loading.rmBoms}
                  onClick={() => setBomPage(bomTotalPages)}
                  data-testid="rm-bom-page-last"
                >Last</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Bidso Reviews Tab — review queue from the Colourway Composer */}
        <TabsContent value="bidso-reviews">
          <BidsoReviewsTab />
        </TabsContent>

        {/* Brand BOM Import Reviews (Jul 2026) — Demand Planner → Tech Ops. */}
        <TabsContent value="brand-bom-reviews">
          <BrandBomImportReviewsTab />
        </TabsContent>
      </Tabs>

      {/* Vertical Dialog */}
      <Dialog open={showVerticalDialog} onOpenChange={(open) => { setShowVerticalDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Vertical' : 'Create Vertical'}</DialogTitle>
            <DialogDescription>Product category grouping (e.g., Scooter, Trike, Walker)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input 
                value={verticalForm.code}
                onChange={(e) => setVerticalForm({...verticalForm, code: e.target.value})}
                placeholder="e.g., SCOOTER"
                className="font-mono uppercase"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                value={verticalForm.name}
                onChange={(e) => setVerticalForm({...verticalForm, name: e.target.value})}
                placeholder="Display name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={verticalForm.description}
                onChange={(e) => setVerticalForm({...verticalForm, description: e.target.value})}
              />
            </div>
            <Button onClick={handleCreateVertical} className="w-full">{editingItem ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Model Dialog */}
      <Dialog open={showModelDialog} onOpenChange={(open) => { setShowModelDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Model' : 'Create Model'}</DialogTitle>
            <DialogDescription>Product model under a vertical (e.g., Blaze, Astro)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vertical</Label>
              <Select value={modelForm.vertical_id} onValueChange={(v) => setModelForm({...modelForm, vertical_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vertical" />
                </SelectTrigger>
                <SelectContent>
                  {verticals.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Code</Label>
              <Input 
                value={modelForm.code}
                onChange={(e) => setModelForm({...modelForm, code: e.target.value})}
                placeholder="e.g., BLAZE"
                className="font-mono uppercase"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                value={modelForm.name}
                onChange={(e) => setModelForm({...modelForm, name: e.target.value})}
              />
            </div>
            <Button onClick={handleCreateModel} className="w-full">{editingItem ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={showBrandDialog} onOpenChange={(open) => { setShowBrandDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Brand' : 'Create Brand'}</DialogTitle>
            <DialogDescription>Brand definition (Code + Name)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input 
                value={brandForm.code}
                onChange={(e) => setBrandForm({...brandForm, code: e.target.value.toUpperCase()})}
                placeholder="e.g., FC"
                className="font-mono uppercase"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Brand Name</Label>
              <Input 
                value={brandForm.name}
                onChange={(e) => setBrandForm({...brandForm, name: e.target.value})}
                placeholder="e.g., Firstcry"
              />
            </div>
            <Button onClick={handleCreateBrand} className="w-full">{editingItem ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* License Dialog (Feb 2026) */}
      <Dialog open={showLicenseDialog} onOpenChange={(open) => { setShowLicenseDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit License' : 'Create License'}</DialogTitle>
            <DialogDescription>
              Licensed IP definition. The <span className="font-mono">CODE</span> is appended into Bidso SKU IDs when a SKU is flagged as licensed (e.g. <span className="font-mono">KS_BE_<b>MARVEL</b>_001</span>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>License Code *</Label>
              <Input
                value={licenseForm.code}
                onChange={(e) => setLicenseForm({ ...licenseForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., MARVEL"
                className="font-mono uppercase"
                maxLength={15}
                data-testid="license-code-input"
              />
            </div>
            <div>
              <Label>License Name *</Label>
              <Input
                value={licenseForm.name}
                onChange={(e) => setLicenseForm({ ...licenseForm, name: e.target.value })}
                placeholder="e.g., Marvel Comics"
                data-testid="license-name-input"
              />
            </div>
            <Button onClick={handleSaveLicense} className="w-full" data-testid="license-save-btn">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyer Dialog */}
      <Dialog open={showBuyerDialog} onOpenChange={(open) => { setShowBuyerDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Buyer' : 'Create Buyer'}</DialogTitle>
            <DialogDescription>
              {editingItem 
                ? `Editing buyer: ${editingItem.customer_code || editingItem.code}` 
                : 'Customer code will be auto-generated (e.g., CUST001)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editingItem && (
              <div>
                <Label>Customer Code</Label>
                <Input 
                  value={editingItem.customer_code || editingItem.code || ''}
                  disabled
                  className="font-mono bg-zinc-100"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-generated, cannot be changed</p>
              </div>
            )}
            <div>
              <Label>Customer Name *</Label>
              <Input 
                value={buyerForm.name}
                onChange={(e) => setBuyerForm({...buyerForm, name: e.target.value})}
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input 
                value={buyerForm.gst}
                onChange={(e) => setBuyerForm({...buyerForm, gst: e.target.value.toUpperCase()})}
                placeholder="e.g., 22AAAAA0000A1Z5"
                className="font-mono uppercase"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                value={buyerForm.email}
                onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                type="email"
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input 
                value={buyerForm.phone_no}
                onChange={(e) => setBuyerForm({...buyerForm, phone_no: e.target.value})}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <Label>POC Name (Point of Contact)</Label>
              <Input 
                value={buyerForm.poc_name}
                onChange={(e) => setBuyerForm({...buyerForm, poc_name: e.target.value})}
                placeholder="Contact person name"
              />
            </div>
            <Button onClick={handleCreateBuyer} className="w-full" disabled={!buyerForm.name.trim()}>
              {editingItem ? 'Update' : 'Create Buyer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyer Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Buyers or Addresses</DialogTitle>
            <DialogDescription>
              Buyers import creates new customers. Address import tags Bill-To / Ship-To addresses onto existing customers (matched by Customer Code).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm font-semibold mb-2">1. Import Buyers</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleBuyerImport}
                className="hidden"
                id="buyer-file-input"
              />
              <label htmlFor="buyer-file-input" className="cursor-pointer">
                <Button variant="outline" disabled={importLoading} asChild>
                  <span data-testid="import-buyers-btn">{importLoading ? 'Importing...' : 'Select Buyers Excel'}</span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-2">Cols: Customer Name (req), GST, Email, Phone No, POC Name</p>
            </div>

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm font-semibold mb-2">2. Import Addresses</div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleAddressImport}
                className="hidden"
                id="address-file-input"
              />
              <label htmlFor="address-file-input" className="cursor-pointer">
                <Button variant="outline" disabled={importLoading} asChild>
                  <span data-testid="import-addresses-btn">{importLoading ? 'Importing...' : 'Select Addresses Excel'}</span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-2">
                Cols: Customer Code, Address Type (BILL_TO/SHIP_TO), Address Label, GSTIN, Contact Name, Contact Phone, Address Line 1/2, City, State (req), Pincode, Country, Is Default
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">Upsert on (Customer Code + Address Type + Address Label). Unknown customers rejected per-row.</p>
              <Button
                variant="ghost" size="sm"
                onClick={handleDownloadAddresses}
                className="mt-2"
                data-testid="download-addresses-btn"
              ><Download className="w-3 h-3 mr-1" /> Download all addresses</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteConfirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action cannot be undone if there are no dependencies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteConfirm?.type === 'vertical') handleDeleteVertical(deleteConfirm.id);
                else if (deleteConfirm?.type === 'model') handleDeleteModel(deleteConfirm.id);
                else if (deleteConfirm?.type === 'brand') handleDeleteBrand(deleteConfirm.id);
                else if (deleteConfirm?.type === 'buyer') handleDeleteBuyer(deleteConfirm.id);
                else if (deleteConfirm?.type === 'license') handleDeleteLicense(deleteConfirm.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* RM Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => { setShowCategoryDialog(open); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit RM Category' : 'Create RM Category'}</DialogTitle>
            <DialogDescription>Define source type, BOM level, UOM, and description columns for this category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category Code *</Label>
                <Input 
                  value={categoryForm.code}
                  onChange={(e) => setCategoryForm({...categoryForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., POLY, MB, INP"
                  className="font-mono uppercase"
                  disabled={!!editingItem}
                />
              </div>
              <div>
                <Label>Name *</Label>
                <Input 
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                  placeholder="e.g., Polymer Grades"
                />
              </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Default UOM</Label>
                <Select 
                  value={categoryForm.default_uom || "PCS"}
                  onValueChange={(v) => setCategoryForm({...categoryForm, default_uom: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select UOM" />
                  </SelectTrigger>
                  <SelectContent>
                    {RM_UOM_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>RM ID Prefix</Label>
                <Input 
                  value={categoryForm.rm_id_prefix}
                  onChange={(e) => setCategoryForm({...categoryForm, rm_id_prefix: e.target.value.toUpperCase()})}
                  placeholder={categoryForm.code || "PREFIX"}
                  className="font-mono uppercase"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  IDs: {categoryForm.rm_id_prefix || categoryForm.code || 'XXX'}_001, _002...
                </p>
              </div>
              <div>
                <Label>Source Type</Label>
                <Select 
                  value={categoryForm.default_source_type || "PURCHASED"}
                  onValueChange={(v) => setCategoryForm({...categoryForm, default_source_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PURCHASED">PURCHASED</SelectItem>
                    <SelectItem value="MANUFACTURED">MANUFACTURED</SelectItem>
                    <SelectItem value="BOTH">BOTH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>BOM Level</Label>
                <Select 
                  value={String(categoryForm.default_bom_level) || "1"}
                  onValueChange={(v) => setCategoryForm({...categoryForm, default_bom_level: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">L1 - Raw</SelectItem>
                    <SelectItem value="2">L2 - Transform 1</SelectItem>
                    <SelectItem value="3">L3 - Transform 2</SelectItem>
                    <SelectItem value="4">L4 - Transform 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zoho Account Type</Label>
                <Select
                  value={categoryForm.zoho_account_type || "__none__"}
                  onValueChange={(v) => setCategoryForm({...categoryForm, zoho_account_type: v === "__none__" ? "" : v})}
                >
                  <SelectTrigger data-testid="zoho-account-type-select">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not set —</SelectItem>
                    <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                    <SelectItem value="Spares/Accessories">Spares/Accessories</SelectItem>
                    <SelectItem value="Packaging Material">Packaging Material</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-fills the GL account on RM Inward bills.
                </p>
              </div>
            </div>
            
            {/* Description Columns Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <Label className="text-sm font-bold">Description Columns</Label>
                  <p className="text-xs text-muted-foreground">
                    Define fields for category_data when creating RMs via upload
                  </p>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addDescriptionColumn}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Column
                </Button>
              </div>
              
              {categoryForm.description_columns?.length > 0 ? (
                <div className="space-y-2">
                  {categoryForm.description_columns.map((col, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-white p-2 rounded border">
                      <Input 
                        value={col.key}
                        onChange={(e) => updateDescriptionColumn(idx, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        placeholder="field_key"
                        className="w-[120px] font-mono text-xs"
                      />
                      <Input 
                        value={col.label}
                        onChange={(e) => updateDescriptionColumn(idx, 'label', e.target.value)}
                        placeholder="Display Label"
                        className="flex-1"
                      />
                      <Select 
                        value={col.type || "text"}
                        onValueChange={(v) => updateDescriptionColumn(idx, 'type', v)}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="select">Select</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1 text-xs">
                        <input 
                          type="checkbox" 
                          checked={col.required} 
                          onChange={(e) => updateDescriptionColumn(idx, 'required', e.target.checked)}
                          className="w-4 h-4"
                        />
                        Req
                      </label>
                      <label className="flex items-center gap-1 text-xs">
                        <input 
                          type="checkbox" 
                          checked={col.include_in_name} 
                          onChange={(e) => updateDescriptionColumn(idx, 'include_in_name', e.target.checked)}
                          className="w-4 h-4"
                        />
                        In Name
                      </label>
                      {/* Feb 2026 (P2) — Manage Options button, select-only.
                          Requires the column to be persisted (editingItem
                          => existing category). Newly-added, unsaved
                          columns show a hint tooltip and stay disabled. */}
                      {col.type === "select" && (
                        editingItem && col.key ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openOptionsDialog(editingItem.code, col)}
                            className="text-xs"
                            data-testid={`manage-options-btn-${col.key}`}
                          >
                            Manage Options ({(col.options || []).length})
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled
                            title="Save the category first, then manage options."
                            className="text-xs opacity-60"
                          >
                            Manage Options
                          </Button>
                        )
                      )}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeDescriptionColumn(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No columns defined. Add columns to configure the RM upload template.
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveCategory} data-testid="save-category-btn">
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 (P2) — Manage Options dialog for a select column.
          Independent of the category form Save button — changes persist
          immediately via the generic /options endpoints. */}
      <Dialog
        open={optionsDialog.open}
        onOpenChange={(open) => setOptionsDialog(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-2xl" data-testid="manage-options-dialog">
          <DialogHeader>
            <DialogTitle>
              {optionsDialog.code} · {optionsDialog.columnLabel} — Manage Options ({optionsDialog.options.length})
            </DialogTitle>
            <DialogDescription>
              Changes save immediately. Type-filter, add, delete, or bulk-replace the option list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!optionsDialog.bulkMode ? (
              <>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search options…"
                    value={optionsDialog.search}
                    onChange={(e) => setOptionsDialog(prev => ({ ...prev, search: e.target.value }))}
                    data-testid="manage-options-search"
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setOptionsDialog(prev => ({ ...prev, bulkMode: true, bulkText: prev.options.join("\n") }))}
                    data-testid="manage-options-bulk-toggle"
                  >
                    Bulk Replace
                  </Button>
                </div>

                <div className="border rounded max-h-72 overflow-y-auto" data-testid="manage-options-list">
                  {(() => {
                    const q = optionsDialog.search.trim().toLowerCase();
                    const filtered = q
                      ? optionsDialog.options.filter(o => (o || "").toLowerCase().includes(q))
                      : optionsDialog.options;
                    if (filtered.length === 0) {
                      return <div className="p-4 text-sm text-zinc-500 text-center">No options match.</div>;
                    }
                    return filtered.map((opt) => (
                      <div key={opt} className="flex items-center justify-between px-3 py-1.5 border-b last:border-b-0 text-sm">
                        <span className="font-mono">{opt}</span>
                        <Button
                          type="button" variant="ghost" size="sm"
                          disabled={optionsDialog.inFlight}
                          onClick={() => handleDeleteOption(opt)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`manage-options-delete-${opt}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ));
                  })()}
                </div>

                <div className="flex gap-2 items-center border-t pt-3">
                  <Input
                    placeholder="Add new option…"
                    value={optionsDialog.newOption}
                    onChange={(e) => setOptionsDialog(prev => ({ ...prev, newOption: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddOption(); } }}
                    data-testid="manage-options-new-input"
                  />
                  <Button
                    type="button"
                    onClick={handleAddOption}
                    disabled={optionsDialog.inFlight || !optionsDialog.newOption.trim()}
                    data-testid="manage-options-add-btn"
                  >
                    Add
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Bulk replace overwrites the entire list. One option per line.
                </div>
                <Textarea
                  value={optionsDialog.bulkText}
                  onChange={(e) => setOptionsDialog(prev => ({ ...prev, bulkText: e.target.value }))}
                  rows={12}
                  className="font-mono text-xs"
                  data-testid="manage-options-bulk-textarea"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button" variant="outline"
                    onClick={() => setOptionsDialog(prev => ({ ...prev, bulkMode: false }))}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button" variant="destructive"
                    onClick={() => setOptionsDialog(prev => ({ ...prev, confirmBulk: true }))}
                    disabled={optionsDialog.inFlight}
                    data-testid="manage-options-bulk-save-btn"
                  >
                    Replace List
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk replace confirmation. */}
      <AlertDialog
        open={optionsDialog.confirmBulk}
        onOpenChange={(open) => setOptionsDialog(prev => ({ ...prev, confirmBulk: open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the entire option list?</AlertDialogTitle>
            <AlertDialogDescription>
              This overwrites the current options for {optionsDialog.code} · {optionsDialog.columnLabel}. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkReplace} data-testid="manage-options-bulk-confirm">
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* RM BOM Dialog */}
      <Dialog open={showBomDialog} onOpenChange={(open) => { setShowBomDialog(open); if (!open) setEditingItem(null); }}>        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit RM BOM' : 'Create RM BOM'}</DialogTitle>
            <DialogDescription>Define the components and quantities needed to produce this RM</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RM ID (Output)</Label>
                <StockItemSearch
                  searchEndpoint={`${API}/raw-materials/search?source_type=NOT_PURCHASED`}
                  value={bomForm.rm_id}
                  onSelect={(item) => {
                    const cat = item.category || item.item_id?.split('_')[0] || '';
                    setBomForm({...bomForm, rm_id: item.item_id, category: cat});
                  }}
                  disabled={!!editingItem}
                  placeholder="Type RM ID (e.g. INP_654)..."
                  testId="bom-output-rm-search"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={bomForm.category || undefined}
                  onValueChange={(v) => setBomForm({...bomForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {rmCategories.filter(c => c.default_source_type !== 'PURCHASED').map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>BOM Level</Label>
                <Select 
                  value={String(bomForm.bom_level) || undefined}
                  onValueChange={(v) => setBomForm({...bomForm, bom_level: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">L2</SelectItem>
                    <SelectItem value="3">L3</SelectItem>
                    <SelectItem value="4">L4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Output Qty</Label>
                <Input 
                  type="number"
                  value={bomForm.output_qty}
                  onChange={(e) => setBomForm({...bomForm, output_qty: parseFloat(e.target.value) || 1})}
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Output UOM</Label>
                <Select 
                  value={bomForm.output_uom || undefined}
                  onValueChange={(v) => setBomForm({...bomForm, output_uom: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="UOM" />
                  </SelectTrigger>
                  <SelectContent>
                    {RM_UOM_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Yield Factor</Label>
                <Input 
                  type="number"
                  value={bomForm.yield_factor}
                  onChange={(e) => setBomForm({...bomForm, yield_factor: parseFloat(e.target.value) || 1})}
                  min="0.01"
                  max="1"
                  step="0.01"
                />
              </div>
            </div>

            {/* Components Section */}
            <div className="border rounded-lg p-4">
              <Label className="text-sm font-bold uppercase mb-2 block">Components (Input Materials)</Label>
              
              {/* Add Component Form */}
              <div className="grid grid-cols-6 gap-2 mb-3 items-end">
                <div className="col-span-2">
                  <Label className="text-xs">RM ID</Label>
                  <StockItemSearch
                    searchEndpoint={`${API}/raw-materials/search?max_bom_level=${Math.max(1, bomForm.bom_level - 1)}`}
                    value={bomComponent.component_rm_id}
                    onSelect={(item) => setBomComponent({...bomComponent, component_rm_id: item.item_id})}
                    placeholder="Type component RM ID..."
                    testId="bom-component-rm-search"
                  />
                </div>
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input 
                    type="number"
                    value={bomComponent.quantity}
                    onChange={(e) => setBomComponent({...bomComponent, quantity: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="0.001"
                    className="text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">UOM</Label>
                  <Select 
                    value={bomComponent.uom || undefined}
                    onValueChange={(v) => setBomComponent({...bomComponent, uom: v})}
                  >
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RM_UOM_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Wastage %</Label>
                  <Input 
                    type="number"
                    value={((bomComponent.wastage_factor - 1) * 100).toFixed(0)}
                    onChange={(e) => setBomComponent({...bomComponent, wastage_factor: 1 + (parseFloat(e.target.value) || 0) / 100})}
                    min="0"
                    max="50"
                    className="text-xs"
                  />
                </div>
                <div>
                  <Button size="sm" onClick={addBomComponent} className="w-full">
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Components List */}
              {bomForm.components.length > 0 && (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">RM ID</th>
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2 text-center">UOM</th>
                        <th className="p-2 text-right">Wastage</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bomForm.components.map((comp, idx) => (
                        <tr key={idx}>
                          <td className="p-2 font-mono">{comp.component_rm_id}</td>
                          <td className="p-2">{comp.component_name || getRmName(comp.component_rm_id)}</td>
                          <td className="p-2 text-right">{comp.quantity}</td>
                          <td className="p-2 text-center">{comp.uom}</td>
                          <td className="p-2 text-right">{((comp.wastage_factor - 1) * 100).toFixed(0)}%</td>
                          <td className="p-2 text-center">
                            <Button variant="ghost" size="sm" onClick={() => removeBomComponent(comp.component_rm_id)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {bomForm.components.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No components added. Add at least one component.</p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowBomDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveBom} data-testid="save-bom-btn">
                {editingItem ? 'Update BOM' : 'Create BOM'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* BOM Bulk Upload Dialog */}
      <Dialog open={bomUploadOpen} onOpenChange={setBomUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Upload RM BOMs</DialogTitle>
            <DialogDescription>
              Upload Excel with columns: RM ID, BOM RM ID, Weight in gm / Pc, Wastage %
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Excel File</Label>
              <Input 
                ref={bomFileRef} 
                type="file" 
                accept=".xlsx,.xls" 
                className="mt-1"
                data-testid="bom-upload-file"
              />
              <Button variant="link" className="px-0 text-xs h-auto mt-1" onClick={downloadBomTemplate} data-testid="download-bom-template">
                <Download className="w-3 h-3 mr-1" /> Download Template
              </Button>
            </div>
            <div>
              <Label>If BOM already exists</Label>
              <Select value={bomUploadMode} onValueChange={setBomUploadMode}>
                <SelectTrigger data-testid="bom-upload-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip (keep existing)</SelectItem>
                  <SelectItem value="replace">Replace (overwrite)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>- Multiple rows with same RM ID are grouped into one BOM</p>
              <p>- UOM auto-detected from component RM category</p>
              <p>- BOM level auto-detected from output RM category</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBomUploadOpen(false)}>Cancel</Button>
              <Button onClick={handleBomBulkUpload} disabled={bomUploading} data-testid="bom-upload-submit">
                {bomUploading ? "Uploading..." : "Upload BOMs"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechOps;
